import { isFetchBaseQueryError } from 'lib-shared-auth';
import type {
  ApiResponse,
  PaginatedApiArg,
  PaginatedApiResponse,
} from 'lib-shared-types-api';

import type {
  AllTemplates,
  Favorites,
  MostUsedTemplates,
  Recommendations,
  RenderedTemplate,
  RenderTemplateContext,
  Template,
  TemplatesSortKey,
  TemplateTranslationsNormalized,
  TemplateTypes,
} from './template.types';

export type CreateTemplateApiArg = {
  readonly content: string;
  readonly name: string;
  readonly type: TemplateTypes;
  readonly subject?: string;
};

export type CreateTemplateApiResponse = ApiResponse<Template>;

export type RenderTemplateApiArg =
  | {
      readonly content: string;
      readonly context: Partial<RenderTemplateContext>;
    }
  | {
      readonly content: string;
      readonly csid: string | number;
    };

export type RenderTemplateApiResponse = ApiResponse<RenderedTemplate>;

export type ReadTemplateByIdApiArg = {
  readonly id: string;
};

export type ReadTemplateByIdApiResponse = ApiResponse<Template>;

export type ReadAllTemplatesApiResponse = PaginatedApiResponse<AllTemplates>;

export type ReadAllTemplatesApiArg = PaginatedApiArg<
  ReadAllTemplatesQueryParams,
  TemplatesSortKey
>;

export type ReadMostUsedTemplatesApiArg = {
  readonly locale?: string;
};

export type ReadMostUsedTemplatesApiResponse = ApiResponse<MostUsedTemplates>;

export type ReadAllTemplatesQueryParams = {
  type: TemplateTypes;
  includeRecommendationForCsid?: string | undefined;
  includeMostUsed?: boolean;
  locale?: string;
  sortBy?: string;
};

export type ReadRecommendationsApiResponse = ApiResponse<Recommendations>;

export type ReadFavoritesApiArg = {
  readonly locale?: string;
};
export type ReadFavoritesApiResponse = ApiResponse<Favorites>;

export type FavoriteTemplateApiArg = {
  readonly id: string;
  readonly locale?: string;
};

export type UnfavoriteTemplateApiArg = FavoriteTemplateApiArg;

type Usage = {
  usage_count: number;
};
export type TrackTemplateUsageResponse = ApiResponse<Usage>;
export type TrackTemplateUsageApiArg = {
  readonly templateId: string;
  readonly locale?: string;
};

export type ReadRecommendationsArg = {
  readonly csid: string;
  readonly locale?: string;
};

export type TemplateLocalesResponse = ApiResponse<{
  readonly locales: string[];
}>;

export type TemplateLocalesApiArg = {
  readonly type: 'quick_response' | 'email_auto_reply';
};

export type UpdateTemplateArg = {
  readonly id: string;
  readonly deletes: string[];
  readonly updates: {
    readonly name: string;
    readonly content?: string;
    readonly subject?: string;
  };
};

export type UpdateTemplateResponse = ApiResponse<Template>;

export type UpdateTemplateTranslationsArg = {
  readonly id: string;
  readonly translations: TemplateTranslationsNormalized;
};

export type UpdateTemplateTranslationsResponse = ApiResponse<Template>;

export type DeleteTemplateApiArg = {
  /** The id of the template to delete. */
  readonly templateId: string;
};

export type DeleteTemplateTranslationsResponse = ApiResponse<Template>;

export type DeleteTemplateTranslationsApiArg = {
  /** The id of the template to delete. */
  readonly templateId: string;
  /** An array of locales to be deleted from the template */
  readonly localesToDelete: string[];
};

/**
 * Type predicate to narrow an unknown error to a boolean to check if a certain
 * template name already exists or not.
 */
export const isConflictError = (error: unknown): boolean => {
  if (isFetchBaseQueryError(error)) return error.status === 409;
  return false;
};
